import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

// Admin and customer sessions are fully separate:
//  - customer token  -> localStorage["token"]  (long-lived SPA convenience)
//  - admin token      -> sessionStorage["admin_token"] (short-lived, per-tab-session)
// Admin-scoped requests use the admin token; everything else uses the customer
// token. A request may force the admin token via `config._adminAuth = true`.
// sessionStorage is per tab, so two tabs = two independent admin sessions: one
// tab can never read, overwrite or clear another tab's token.
api.interceptors.request.use((config) => {
  const url = config.url || "";
  const isAdminUrl = url.startsWith("/admin") || url.startsWith("/auth/admin");
  const useAdmin = config._adminAuth || isAdminUrl;
  const token = useAdmin ? sessionStorage.getItem("admin_token") : localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// A failure that happens before any response is received (offline, DNS/TLS error,
// or a response the browser refuses to hand over because of CORS) has no
// `err.response`. Those are infrastructure/configuration failures and must never
// be mistaken for "the catalogue is empty", so they are reported once, centrally.
export function isNetworkError(err) {
  return !err?.response;
}

// ---------------------------------------------------------------------------
// Admin session classification.
//
// "Your admin session expired" must be shown ONLY when the backend actually
// rejected the session — never for a network drop, a timeout, a 5xx, or an
// unrelated 401/403 (e.g. "Current password is incorrect"). Both helpers below
// are intentionally narrow so the portal can tell the two apart.
// ---------------------------------------------------------------------------
export function isAdminScopedRequest(err) {
  const url = err?.config?.url || "";
  const method = (err?.config?.method || "get").toLowerCase();
  return (
    !!err?.config?._adminAuth ||
    url.startsWith("/admin") ||
    (url.startsWith("/auth/admin") && method !== "options")
  );
}

// Backend phrases that mean "this token/session is not (or no longer) valid".
const ADMIN_SESSION_DETAILS = [
  "session expired",
  "not authenticated",
  "invalid authentication token",
  "account not available",
  "admin access required",
  "admin re-authentication required",
];

export function isAdminSessionError(err) {
  const status = err?.response?.status;
  if (status !== 401 && status !== 403) return false;
  if (!isAdminScopedRequest(err)) return false;
  const detail = String(err?.response?.data?.detail ?? "").toLowerCase();
  return ADMIN_SESSION_DETAILS.some((phrase) => detail.includes(phrase));
}

// Event the admin auth context listens to. Dispatched only for a genuine
// backend rejection of an admin session while a token is present.
export const ADMIN_SESSION_INVALID_EVENT = "admin-session-invalid";

api.interceptors.response.use(
  (response) => response,
  (err) => {
    const method = (err?.config?.method || "get").toUpperCase();
    const url = err?.config?.url || "";
    const reason = isNetworkError(err)
      ? "no response from the API (network, offline, or blocked by CORS)"
      : `HTTP ${err.response.status}`;
    console.error(`[api] ${method} ${url} failed: ${reason}`);
    if (isAdminSessionError(err)) {
      let hasToken = false;
      try { hasToken = !!sessionStorage.getItem("admin_token"); } catch { hasToken = false; }
      if (hasToken) {
        try {
          window.dispatchEvent(new CustomEvent(ADMIN_SESSION_INVALID_EVENT, {
            detail: { status: err.response.status, message: apiError(err) },
          }));
        } catch (e) {
          console.debug("admin session event could not be dispatched:", e?.message);
        }
      }
    }
    return Promise.reject(err);
  }
);

export function apiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Human-readable reason for an admin request that failed, distinguishing a real
// session rejection from a temporary/technical failure.
export function adminFailureReason(err) {
  if (isAdminSessionError(err)) return apiError(err);
  if (isNetworkError(err)) {
    return "Could not reach the server (network, offline or blocked by CORS). Nothing on the server changed — try again.";
  }
  if (err?.response?.status === 409) return apiError(err);
  if (err?.response?.status >= 500) {
    return `The server returned an error (HTTP ${err.response.status}). Nothing was reloaded — try again.`;
  }
  return apiError(err);
}

export default api;
